import React from "react";
import { Msr } from "./components";

// Per-family statement — "what does each family owe?" (backend
// helpers/oireachtasStatement.js builds it; this only renders).
//
// The payment summary elsewhere groups by fee TYPE, which totals the school's
// bill but can't answer the question a teacher actually gets asked. This shows
// the same money grouped by who owes it: the family fee, then each of that
// family's dancers with their entries itemised, then the family total.
//
// School charges (assessment, service fee, late surcharge, reclaim overage) are
// listed separately and deliberately NOT billed on to families (Devin,
// 2026-08-13). Nothing here is apportioned, so the family totals plus the
// school lines reconcile exactly against the school's bill.

const fmt = (cents, cur) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: cur || "CAD" }).format(
    (cents || 0) / 100
  );

/** CSV rows (including the header) — one row per line item so it sorts and
 *  filters in a spreadsheet, which a nested layout would not. */
export function statementCsvRows(statement, currency) {
  const rows = [["Family", "Dancer", "Item", "Amount", "Status"]];
  if (!statement) return rows;
  const amount = (c) => ((c || 0) / 100).toFixed(2);
  const status = (paid) => (paid ? "Paid" : "Unpaid");

  const group = (label, fam) => {
    if (fam.familyFee) {
      rows.push([
        label,
        "",
        fam.familyFee.waived ? "Family fee (waived)" : "Family fee",
        amount(fam.familyFee.amountCents),
        fam.familyFee.waived ? "Waived" : status(fam.familyFee.paid),
      ]);
    }
    fam.dancers.forEach((d) =>
      d.lines.forEach((l) =>
        rows.push([label, d.name, l.label, amount(l.amountCents), status(l.paid)])
      )
    );
    rows.push([label, "", "Family total", amount(fam.totalCents), ""]);
  };

  statement.families.forEach((f) => group(f.label, f));
  if (statement.unlinked) group("No family linked", statement.unlinked);
  (statement.schoolLines || []).forEach((l) =>
    rows.push(["School (not billed to families)", "", l.label, amount(l.totalCents), ""])
  );
  rows.push(["", "", "Grand total", amount(statement.totals.grandTotalCents), ""]);
  return rows;
}

function Line({ label, cents, paid, waived, currency, muted, pills = true }) {
  return (
    <div className={"fst-line" + (muted ? " muted" : "")}>
      <span className="k">
        {label}
        {waived && <span className="fst-pill waived">Waived</span>}
        {pills && !waived && paid === false && <span className="fst-pill unpaid">Unpaid</span>}
      </span>
      <span className="v">{fmt(cents, currency)}</span>
    </div>
  );
}

function FamilyBlock({ label, fam, currency, pills = true }) {
  return (
    <div className="fst-fam">
      <div className="fst-fh">
        <span className="nm">{label}</span>
        <span className="tot">
          {fmt(fam.totalCents, currency)}
          {fam.unpaidCents > 0 && fam.paidCents > 0 && (
            <span className="split">{fmt(fam.unpaidCents, currency)} unpaid</span>
          )}
        </span>
      </div>

      {fam.familyFee && (
        <Line
          label="Family fee"
          cents={fam.familyFee.amountCents}
          paid={fam.familyFee.paid}
          waived={fam.familyFee.waived}
          currency={currency}
          pills={pills}
        />
      )}

      {fam.dancers.map((d) => (
        <div className="fst-dancer" key={d.dancerId}>
          <div className="fst-dh">
            <span className="nm">{d.name}</span>
            <span className="v">{fmt(d.totalCents, currency)}</span>
          </div>
          {d.lines.map((l, i) => (
            <Line
              key={i}
              label={l.label}
              cents={l.amountCents}
              paid={l.paid}
              currency={currency}
              pills={pills}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Whether a statement has anything to show. School charges alone count — a
 *  reclaim- or service-fee-only checkout has no families at all. Callers that
 *  swap between this and a fallback layout MUST use this rather than testing
 *  the object, or they hide their fallback behind an empty render. */
export function hasStatementContent(statement) {
  return !!(
    statement &&
    (statement.families.length ||
      statement.unlinked ||
      (statement.schoolLines || []).length)
  );
}

// pills=false suppresses the per-line "Unpaid" chips — on a pre-payment quote
// every line is unpaid by definition, so the chips are 50 rows of noise.
export default function FamilyStatement({ statement, currency, pills = true }) {
  if (!hasStatementContent(statement)) return null;

  return (
    <div className="fst">
      {statement.families.map((f) => (
        <FamilyBlock key={f.familyId} label={f.label} fam={f} currency={currency} pills={pills} />
      ))}

      {statement.unlinked && (
        <FamilyBlock label="No family linked" fam={statement.unlinked} currency={currency} pills={pills} />
      )}

      {(statement.schoolLines || []).length > 0 && (
        <div className="fst-fam school">
          <div className="fst-fh">
            <span className="nm">
              <Msr>school</Msr>School charges
            </span>
            <span className="tot">{fmt(statement.totals.schoolCents, currency)}</span>
          </div>
          <div className="fst-note">Not billed to families.</div>
          {statement.schoolLines.map((l) => (
            <Line key={l.type} label={l.label} cents={l.totalCents} currency={currency} muted />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Past payments, newest first — one frozen receipt each. A school registers
 * over several sittings, so a teacher needs to come back for any of them long
 * after the confirmation email has been lost.
 *
 * Each row expands to the statement AS ISSUED: never recomputed, so a dancer
 * renamed afterwards doesn't alter a receipt a family already has.
 */
export function ReceiptList({ receipts, currency, onDownload }) {
  const [open, setOpen] = React.useState(null);
  if (!receipts || !receipts.length) return null;

  const when = (d) =>
    new Date(d).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" });

  return (
    <div className="fst rcpts">
      {receipts.map((r) => {
        const isOpen = open === r.paymentId;
        return (
          <div className="fst-fam" key={r.paymentId}>
            <div className="fst-fh rc-h" onClick={() => setOpen(isOpen ? null : r.paymentId)}>
              <span className="nm">
                <Msr>receipt_long</Msr>
                {r.receiptNumber ? `Receipt #${r.receiptNumber}` : "Receipt"}
                <span className="rc-when">{when(r.paidAt)}</span>
              </span>
              <span className="tot">
                {fmt(r.totalCents, r.currency || currency)}
                <span className="rc-act">
                  <button
                    type="button"
                    className="rc-dl"
                    onClick={(e) => { e.stopPropagation(); onDownload(r); }}
                  >
                    <Msr>file_download</Msr>CSV
                  </button>
                </span>
              </span>
            </div>
            {isOpen && (
              r.statement ? (
                <FamilyStatement statement={r.statement} currency={r.currency || currency} />
              ) : (
                <div className="fst-note">
                  This payment predates itemised receipts; its total is shown above.
                </div>
              )
            )}
          </div>
        );
      })}
    </div>
  );
}
